import { useQuery } from 'react-query';
import { useSelector } from 'react-redux';

import { CommonSellerService } from '@savgroup-front-common/core/api/Seller';
import { selectLocale } from '@savgroup-front-common/core/domains/i18n/selectors';

import { adaptCompanyProfileToActivities } from '../adapters/adaptCompanyProfileToActivities';
import {
  PickedCompany,
  ProposedActivityProfile,
} from '../OnboardingActivities.types';

export const COMPANY_PROFILE_QUERY_KEY = 'suggestCompanyProfile';

interface UseCompanyProfileSuggestionReturnValues {
  proposedProfile: ProposedActivityProfile;
  isLoading: boolean;
  hasSuggestionFailed: boolean;
  hasPickedCompany: boolean;
}

export function useCompanyProfileSuggestion({
  pickedCompany,
}: {
  pickedCompany?: PickedCompany;
}): UseCompanyProfileSuggestionReturnValues {
  const locale = useSelector(selectLocale);

  const { data, isLoading } = useQuery(
    [COMPANY_PROFILE_QUERY_KEY, { pickedCompany, locale }],
    async () => {
      if (!pickedCompany) {
        return {
          proposedProfile: adaptCompanyProfileToActivities({}),
          hasFailed: false,
        };
      }

      const response = await CommonSellerService.suggestCompanyProfileCommand({
        source: pickedCompany.source,
        registrationNumber: pickedCompany.registrationNumber,
        language: locale,
      });

      if (response.failure) {
        return {
          proposedProfile: adaptCompanyProfileToActivities({}),
          hasFailed: true,
        };
      }

      return {
        proposedProfile: adaptCompanyProfileToActivities({
          suggestion: response.value,
        }),
        hasFailed: false,
      };
    },
    { enabled: !!pickedCompany, staleTime: Infinity },
  );

  return {
    proposedProfile:
      data?.proposedProfile ?? adaptCompanyProfileToActivities({}),
    isLoading,
    hasSuggestionFailed: data?.hasFailed ?? false,
    hasPickedCompany: !!pickedCompany,
  };
}
